import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { useEffect } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { getErrorMessage } from "utils/form"
import { ProductDto } from "@custom-types/products"
import { SimulationFrequency } from "@features/builder/types"
import { MetadataFormSectionCard } from "@features/builder/UpdateMetadata/MetadataFormSectionCard"
import { type MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import { useGetProductsQuery } from "@store/services/productsService"
import { METADATA_FORM_SECTIONS, getEndDateMinDate } from "@features/builder/UpdateMetadata/helpers"

export type MetadataFormDetailsSectionProps = {
  graphDetails?: string
}

const simFrequencies = [
  { id: "yearly", value: "Yearly" },
  { id: "quarterly", value: "Quarterly" },
  { id: "monthly", value: "Monthly" },
] satisfies { id: SimulationFrequency; value: string }[]

export const MetadataFormDetailsSection = ({ graphDetails }: MetadataFormDetailsSectionProps) => {
  const { data: products, isLoading: isLoadingProducts } = useGetProductsQuery({
    roleHint: "build",
    createMode: true,
  })
  const productList = products?.items || []

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext<MetadataFormValues>()

  const simulationFrequency = watch("simulation_frequency")
  const rollingStartMonth = watch("yearly_rolling_start_month")
  const startDate = watch("start_date")
  const endDate = watch("end_date")
  const endDateMinDate = getEndDateMinDate(startDate)

  useEffect(() => {
    // The schema compares both dates, so changing start_date must refresh any stale end_date error.
    if (isSubmitted) trigger("end_date")
  }, [startDate, endDate, trigger, isSubmitted])

  // When simulation frequency changes, adjust yearly rolling start month accordingly
  useEffect(() => {
    if (simulationFrequency !== "yearly") {
      setValue("yearly_rolling_start_month", undefined)
      return
    }

    if (simulationFrequency === "yearly" && !rollingStartMonth) {
      setValue("yearly_rolling_start_month", 1)
      return
    }
  }, [simulationFrequency, rollingStartMonth, setValue])

  return (
    <MetadataFormSectionCard id={METADATA_FORM_SECTIONS.details} title="Details">
      <Stack direction="column" spacing={2}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              disabled={graphDetails !== undefined}
              error={Boolean(errors[field.name])}
              helperText={getErrorMessage(errors, field.name)}
              label="Name"
              variant="standard"
              sx={{ spacing: 2 }}
              {...field}
            />
          )}
        />
        <Controller
          name="product_key"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                disabled={graphDetails !== undefined}
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                select={true}
                label="Product"
                variant="standard"
                {...field}
              >
                {isLoadingProducts && field.value ? (
                  <MenuItem value={field.value}>{field.value}</MenuItem>
                ) : (
                  productList.map((product: ProductDto) => (
                    <MenuItem
                      key={product.key}
                      value={
                        product.organisation_key + "#" + product.division_key + "#" + product.key
                      }
                    >
                      <div>
                        {product.organisation_key + " → " + product.division_key + " → "}
                        <b> {product.key} </b>
                      </div>
                    </MenuItem>
                  ))
                )}
              </TextField>
              <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
            </>
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              sx={{ marginTop: "0 !important" }}
              error={Boolean(errors[field.name])}
              helperText={getErrorMessage(errors, field.name)}
              label="Description"
              variant="standard"
              {...field}
            />
          )}
        />
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            field.value === "rolling" ? (
              <FormControl variant="standard" error={Boolean(errors[field.name])}>
                <TextField
                  error={Boolean(errors[field.name])}
                  helperText={getErrorMessage(errors, field.name)}
                  label="Start date"
                  variant="standard"
                  disabled
                  {...field}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === "rolling"}
                      onClick={() => field.onChange(field.value === "rolling" ? null : "rolling")}
                    />
                  }
                  label="Rolling"
                />
              </FormControl>
            ) : (
              <FormControl variant="standard" error={Boolean(errors[field.name])}>
                <DatePicker
                  label="Start date"
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "standard" } }}
                  {...field}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === "rolling"}
                      onClick={() => field.onChange(field.value === "rolling" ? null : "rolling")}
                    />
                  }
                  label="Rolling"
                />
                <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
              </FormControl>
            )
          }
        />
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            field.value === "rolling" ? (
              <FormControl variant="standard" error={Boolean(errors[field.name])}>
                <TextField
                  error={Boolean(errors[field.name])}
                  helperText={getErrorMessage(errors, field.name)}
                  label="End date"
                  variant="standard"
                  disabled
                  {...field}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === "rolling"}
                      onClick={() => field.onChange(field.value === "rolling" ? null : "rolling")}
                    />
                  }
                  label="Rolling"
                />
              </FormControl>
            ) : (
              <FormControl variant="standard" error={Boolean(errors[field.name])}>
                <DatePicker
                  label="End date"
                  format="yyyy-MM-dd"
                  minDate={endDateMinDate}
                  slotProps={{
                    textField: {
                      variant: "standard",
                      // Mui date picker shows errors on component mount,
                      // but we want to hide issues from the user until he will hit submit button.
                      // Inherit errors from form
                      error: !!errors.end_date?.message,
                    },
                  }}
                  {...field}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value === "rolling"}
                      onClick={() => field.onChange(field.value === "rolling" ? null : "rolling")}
                    />
                  }
                  label="Rolling"
                />
                <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
              </FormControl>
            )
          }
        />
        <Controller
          name="simulation_frequency"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                select={true}
                label="Simulation frequency"
                variant="standard"
                {...field}
              >
                {simFrequencies.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.value}
                  </MenuItem>
                ))}
              </TextField>
              <FormHelperText error>{getErrorMessage(errors, field.name)}</FormHelperText>
            </>
          )}
        />
        {simulationFrequency === "yearly" && (
          <Controller
            name="yearly_rolling_start_month"
            control={control}
            render={({ field }) => (
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                type="number"
                inputProps={{
                  min: 1,
                  max: 12,
                }}
                label="Yearly Start Month"
                variant="standard"
                {...field}
              />
            )}
          />
        )}
        {(!startDate || startDate === "rolling" || !endDate || endDate === "rolling") && (
          <Controller
            name="rolling_duration"
            control={control}
            render={({ field }) => (
              <TextField
                error={Boolean(errors[field.name])}
                helperText={getErrorMessage(errors, field.name)}
                type={"number"}
                defaultValue={1}
                inputProps={{
                  min: 1,
                  max: 9999,
                }}
                label="Rolling Duration"
                variant="standard"
                {...field}
              />
            )}
          />
        )}
      </Stack>
    </MetadataFormSectionCard>
  )
}

export default MetadataFormDetailsSection
